import { useGetTopAssetsQuery } from '@carboncopyco/data';
import { type TopAssetType, TopAssetTypes } from '@carboncopyco/types';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useConsoleState } from './ConsoleContext';
import { TopAssetsTable } from './TopAssetsTable';
import { TopAssetsTableControls } from './TopAssetsTableControls';
import { TopAssetsTableRowWrapper } from './TopAssetsTableRowWrapper';
import { emptyStateMessages } from './consts';
import { useTableControls } from './useTopAssetsTableControls';
import { getTypeFilterOptionsByAvailableAssets } from './utils';

export const TeamTopAssetsTable = () => {
  const { organizationId } = useParams();
  const { startTime, endTime } = useConsoleState();
  const { sort, selectedTypes } = useTableControls();

  const assetTypes = useMemo(
    () => Object.keys(selectedTypes) as TopAssetType[],
    [selectedTypes]
  );

  const teamTopAssetsArgs = useMemo(
    () => ({
      organizationId,
      myAssets: false,
      excludeFavorites: true,
      startTime,
      endTime,
      assetTypes,
      sort,
    }),
    [organizationId, startTime, endTime, assetTypes, sort]
  );

  const {
    data: { items: assets = [] } = {},
    isLoading,
  } = useGetTopAssetsQuery(teamTopAssetsArgs);

  const getFavoritesArgs = useMemo(
    () => ({
      organizationId,
      startTime,
      endTime,
      assetTypes,
    }),
    [organizationId, startTime, endTime, assetTypes]
  );

  const {
    data: { items: unfilteredAssetsWithoutFavorites = [] } = {},
  } = useGetTopAssetsQuery({
    ...teamTopAssetsArgs,
    assetTypes: Object.values(TopAssetTypes),
  });

  const {
    data: { items: unfilteredAssetsWithFavorites } = {},
  } = useGetTopAssetsQuery({
    ...teamTopAssetsArgs,
    excludeFavorites: false,
    assetTypes: Object.values(TopAssetTypes),
  });

  const isEmpty = !isLoading && !assets.length;

  const isEmptyDueToAllAssetsMarkedAsFavorite =
    !!unfilteredAssetsWithFavorites?.length &&
    !unfilteredAssetsWithoutFavorites?.length;

  const typeFilterOptions = getTypeFilterOptionsByAvailableAssets(
    unfilteredAssetsWithoutFavorites
  );

  return (
    <div className="grow overflow-hidden min-h-[300px]">
      <TopAssetsTable
        emptyMessage={
          isEmptyDueToAllAssetsMarkedAsFavorite
            ? emptyStateMessages.isEmptyDueToAllAssetsMarkedAsFavorite
            : undefined
        }
        title="Team resources"
        topAssets={assets}
        isLoading={isLoading}
        renderRow={({ item: topAsset, children }) => (
          <TopAssetsTableRowWrapper
            asset={topAsset}
            getTopAssetsArgs={teamTopAssetsArgs}
            getFavoritesArgs={getFavoritesArgs}
          >
            {children}
          </TopAssetsTableRowWrapper>
        )}
        controls={
          <TopAssetsTableControls
            isEmpty={isEmpty}
            typeFilterOptions={typeFilterOptions}
          />
        }
      />
    </div>
  );
};
